import React, { createElement } from 'react';
import './index.css';
import { render } from 'react-dom';
import { App } from './App';
// Simple schema for basic AI crawlers (Perplexity, etc.)
const SIMPLE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'Viridia Analytics LLC',
  alternateName: 'Viridia Analytics',
  description: 'Phoenix marketing agency specializing in AI-powered lead automation and advertising management for home service contractors.',
  url: 'https://viridiaanalytics.com',
  telephone: '+1-623-300-0708',
  email: 'info@viridiaanalytics.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Phoenix',
    addressRegion: 'AZ',
    postalCode: '85050',
    addressCountry: 'US'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '33.7184',
    longitude: '-112.0785'
  },
  areaServed: [{
    '@type': 'City',
    name: 'Phoenix'
  }, {
    '@type': 'City',
    name: 'Los Angeles'
  }],
  founder: {
    '@type': 'Person',
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO'
  },
  slogan: 'Faster Leads. More Booked Appointments. No Guesswork.',
  priceRange: '$$'
};
// Rich schema with full @graph for advanced crawlers
const STATIC_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'ProfessionalService',
    '@id': 'https://viridiaanalytics.com/#organization',
    name: 'Viridia Analytics LLC',
    alternateName: 'Viridia Analytics',
    description: 'Phoenix marketing agency specializing in AI lead conversion, paid ad campaigns, and web design for local home service businesses like HVAC, plumbing, roofing, and landscaping.',
    url: 'https://viridiaanalytics.com',
    telephone: '+1-623-300-0708',
    email: 'info@viridiaanalytics.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Phoenix',
      addressRegion: 'AZ',
      postalCode: '85050',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '33.7184',
      longitude: '-112.0785'
    },
    foundingDate: '2025',
    founder: {
      '@id': 'https://viridiaanalytics.com/#founder'
    },
    areaServed: [{
      '@type': 'City',
      name: 'Phoenix'
    }, {
      '@type': 'City',
      name: 'Scottsdale'
    }, {
      '@type': 'City',
      name: 'Glendale'
    }, {
      '@type': 'City',
      name: 'Peoria'
    }, {
      '@type': 'City',
      name: 'Chandler'
    }, {
      '@type': 'City',
      name: 'Mesa'
    }, {
      '@type': 'City',
      name: 'Queen Creek'
    }, {
      '@type': 'City',
      name: 'Buckeye'
    }, {
      '@type': 'City',
      name: 'Long Beach'
    }, {
      '@type': 'City',
      name: 'Los Angeles'
    }],
    priceRange: '$$',
    sameAs: ['https://www.facebook.com/profile.php?id=61571074363261', 'https://www.instagram.com/viridiaanalyticsllc/', 'https://www.linkedin.com/company/viridia-analytics/', 'https://www.yelp.com/biz/viridia-analytics-phoenix-2'],
    knowsAbout: ['AI lead conversion', 'Yelp advertising', 'Google Ads management', 'local SEO', 'home services marketing', 'marketing automation', 'CRM systems', 'reputation management'],
    slogan: 'Faster Leads. More Booked Appointments. No Guesswork.',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Marketing Services',
      itemListElement: [{
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'AI Lead Conversion',
          description: 'AI-powered lead response, automated follow-up, and lead nurturing systems that respond in under 60 seconds, 24/7.',
          url: 'https://viridiaanalytics.com/ai-lead-conversion'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Custom Automations',
          description: 'Bespoke automation workflows and API integrations to connect tools and eliminate manual data entry for local businesses.',
          url: 'https://viridiaanalytics.com/custom-automations'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CRM & Business Tools',
          description: 'All-in-one management system to centralize leads, conversations, appointments, and invoicing for service businesses.',
          url: 'https://viridiaanalytics.com/crm-business-tools'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Reputation Management',
          description: 'Automated review requests and management across Google, Facebook, and major review platforms to build trust and local authority.',
          url: 'https://viridiaanalytics.com/reputation-management'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Ad Campaign Management',
          description: 'Google Ads, Meta Ads, and local lead platform management for businesses to drive qualified leads and booked jobs.',
          url: 'https://viridiaanalytics.com/ad-campaigns'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Web Design & SEO',
          description: 'Fast, conversion-focused websites with local SEO optimization to turn searches into customers in Phoenix and beyond.',
          url: 'https://viridiaanalytics.com/web-design-seo'
        }
      }, {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Google Business Profile Setup',
          description: 'Professional setup, optimization, and verification of Google Business Profiles for local visibility and Map Pack rankings.',
          url: 'https://viridiaanalytics.com/google-business-profile-setup'
        }
      }]
    }
  }, {
    '@type': 'Person',
    '@id': 'https://viridiaanalytics.com/#founder',
    name: 'Kevin Rogers',
    jobTitle: 'Founder & CEO',
    worksFor: {
      '@id': 'https://viridiaanalytics.com/#organization'
    },
    description: 'Founder of Viridia Analytics. Former shower installer and cabinet finisher turned ad platform insider, specializing in AI-powered lead conversion and digital advertising for local service businesses and contractors.',
    sameAs: ['https://www.linkedin.com/in/kevinrogers-viridia/']
  }, {
    '@type': 'WebSite',
    '@id': 'https://viridiaanalytics.com/#website',
    name: 'Viridia Analytics',
    url: 'https://viridiaanalytics.com',
    publisher: {
      '@id': 'https://viridiaanalytics.com/#organization'
    },
    description: 'Phoenix marketing agency helping home service businesses grow with AI lead conversion, paid ad campaigns, SEO, and automation.'
  }]
};
// Inject simple schema first (for basic crawlers like Perplexity)
const simpleScriptEl = document.createElement('script');
simpleScriptEl.type = 'application/ld+json';
simpleScriptEl.id = 'simple-business-schema';
simpleScriptEl.textContent = JSON.stringify(SIMPLE_SCHEMA);
document.head.appendChild(simpleScriptEl);
// Inject rich schema second (for advanced crawlers)
const richScriptEl = document.createElement('script');
richScriptEl.type = 'application/ld+json';
richScriptEl.id = 'static-business-schema';
richScriptEl.textContent = JSON.stringify(STATIC_SCHEMA);
document.head.appendChild(richScriptEl);
render(<App data-id="element-330" data-mp="3nps88l5p3815" />, document.getElementById('root'));